import React from 'react';

import ContentBody from '../../../common/contentBody';
import { hasContent } from '../../../common/composer/content';

/**
 * DetailsView — a title and the prose under it, read-only.
 *
 * The rendering of the Details section when nobody can type into it, whatever the reason: a cell
 * past draft, a mission on a committed revision, a closed session. Extracted from CellDetailsView
 * when missions needed it too — a mission's title and description ARE the same pair of fields, and
 * a second rendering of them is how the mission panel and the cell panel come to disagree about
 * what read-only prose looks like.
 *
 * FULL-WIDTH fields, not a two-column table. The gray-chip `.properties` table is right for the
 * short scalar facts around it — a Cell ID, a status, a workspace folder all fit on one line beside
 * their name — but a title and a description are prose somebody wrote, and this pair can run long.
 * Given a 30% name column they wrapped inside a third of the panel while the rest of it sat empty.
 * So the name goes ABOVE and the value gets the whole width. It also puts this section within a
 * pixel of how it looks while the fields ARE editable and are two full-width textareas, so a cell
 * being activated — or a revision being committed — no longer rearranges the section under you.
 *
 * A description is CONTENT — the ordered text/file blocks a thread item carries — so the prose under the
 * title can hold images where their author put them. A bare string is one text block, which is why every
 * description written before this renders through the same path.
 *
 * The fields are named `Title` and `Description`, which is what the orchestrator, the seeds and the
 * editors all call them.
 *
 * Props:
 *   title       — string; the caller supplies its own fallback for an absent one
 *   description — content (a string or text/file blocks); optional
 *   fileContext — { api, revisionId, sessionId }; without it a file block cannot fetch its bytes
 */
export default function DetailsView({ title, description, fileContext }) {
  // Two plain lines: the title in bold, the description under it. Nothing else. Naming the fields
  // cost two labels to say what weight already says, and boxing them dressed read-only prose as a
  // form somebody could type into. The section label above is the only chrome this needs — a rule
  // between the lines and a muted description were both tried, and both only added furniture.
  //
  // `text-prewrap` keeps the line breaks its author typed: this is prose, and collapsed it reads as
  // one run-on paragraph. `text-break` is what stops a pasted URL — the one thing in a description
  // with no space to wrap at — from pushing the panel wider than its column.
  return (
    <div className="mb-2">
      <div className="font-weight-bold text-prewrap text-break">{title}</div>
      {/* A description is optional — a mission is ordinarily sketched out as titles alone — so an
          absent one costs no line at all. */}
      {hasContent(description) && (
        <ContentBody content={description} fileContext={fileContext} className="text-prewrap text-break mt-1" />
      )}
    </div>
  );
}
